import React from 'react';
import { useRecoilState } from 'recoil';
import {
  Box, Heading, FormControl, FormLabel, Select, HStack, Button,
  Tooltip, Icon
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { formDataState } from '../../state/calculatorState';
import { locationValues, rigCapacityValues } from '../../utils/complexityCalculator';

const TOOLTIPS = {
  location: 'Type of drilling location environment (Offshore, Remote, Rural, Urban)',
  residential: 'Site is within or near residential areas, increasing sensitivity requirements',
  sensitive: 'Site has environmental or regulatory sensitivity constraints',
  rigCapacity: 'Drilling rig capacity classification based on depth and load capabilities'
};

const LabelWithInfo = ({ children, tooltip }) => (
  <HStack spacing={1}>
    <span>{children}</span>
    <Tooltip label={tooltip} placement="top" hasArrow>
      <span><Icon as={InfoIcon} boxSize={3} color="gray.400" cursor="help" /></span>
    </Tooltip>
  </HStack>
);

export const SiteLevelSection = ({ compact }) => {
  const [formData, setFormData] = useRecoilState(formDataState);

  const handleChange = (field, value) => {
    // #3: When Urban is selected, auto-enable residential
    if (field === 'location' && value === 'medium') {
      setFormData(prev => ({ ...prev, [field]: value, residential: true }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleBoolToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Box bg="white" p={compact ? 0 : 5} borderRadius={compact ? 0 : 'lg'} boxShadow={compact ? 'none' : 'sm'}>
      {!compact && <Heading size="md" mb={4} color="brand.500">Site Level</Heading>}

      <FormControl mb={3}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.location}>Location</LabelWithInfo>
        </FormLabel>
        <Select
          size="sm"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
        >
          <option value="" disabled>Select...</option>
          {Object.entries(locationValues).map(([key, { txt }]) => (
            <option key={key} value={key}>{txt}</option>
          ))}
        </Select>
      </FormControl>

      <HStack spacing={2} mb={3}>
        <Tooltip label={TOOLTIPS.residential} placement="top" hasArrow>
          <Button
            size="xs"
            role="switch"
            aria-checked={formData.residential}
            variant={formData.residential ? 'solid' : 'outline'}
            colorScheme={formData.residential ? 'blue' : 'gray'}
            onClick={() => handleBoolToggle('residential')}
          >
            Residential
          </Button>
        </Tooltip>
        <Tooltip label={TOOLTIPS.sensitive} placement="top" hasArrow>
          <Button
            size="xs"
            role="switch"
            aria-checked={formData.sensitive}
            variant={formData.sensitive ? 'solid' : 'outline'}
            colorScheme={formData.sensitive ? 'blue' : 'gray'}
            onClick={() => handleBoolToggle('sensitive')}
          >
            Sensitive
          </Button>
        </Tooltip>
      </HStack>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.rigCapacity}>Rig Capacity</LabelWithInfo>
        </FormLabel>
        <Select
          size="sm"
          value={formData.rigCapacity}
          onChange={(e) => handleChange('rigCapacity', e.target.value)}
        >
          <option value="" disabled>Select...</option>
          {Object.entries(rigCapacityValues).map(([key, { txt }]) => (
            <option key={key} value={key}>{txt}</option>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SiteLevelSection;
